'use client'

import { Pip } from '@/design-system'
import { useMockPhase } from './hooks'

const MONO: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontWeight: 500 }

interface Skin {
  bg: string
  accent: string
  name: string
  domain: string
  tag: string
}

const SKINS: readonly Skin[] = [
  {
    bg: 'var(--mauve)',
    accent: 'var(--ink)',
    name: 'OAK & EMBER',
    domain: 'files.oak-ember.co',
    tag: 'Photography studio'
  },
  { bg: 'var(--sky)', accent: 'var(--hot)', name: 'PARALLAX/CO', domain: 'files.parallax.co', tag: 'Design agency' },
  {
    bg: 'var(--acid)',
    accent: 'var(--ink)',
    name: 'NORTHFIELD',
    domain: 'files.northfield.studio',
    tag: 'Architecture'
  }
]

const FILES = [
  ['Q2-final-selects.zip', '1.8 GB'],
  ['contract-signed.pdf', '240 KB'],
  ['notes-for-print.txt', '4 KB']
] as const

interface BrandingMockProps {
  /** When set, the mock is scrubbed externally instead of running its own time loop. */
  phase?: number
}

export function BrandingMock({ phase: controlledPhase }: BrandingMockProps = {}) {
  // Reduced-motion fallback shows skin index 1 (Parallax) — middle/representative.
  const loopPhase = useMockPhase(9000, 0.5, controlledPhase !== undefined)
  const phase = controlledPhase ?? loopPhase
  const skinIdx = phase < 0.34 ? 0 : phase < 0.67 ? 1 : 2
  const s = SKINS[skinIdx]
  const accentTextOnPaper = s.accent === 'var(--ink)' ? 'var(--paper)' : 'var(--ink)'

  return (
    <div
      role="img"
      aria-label="Animation: branded download page cycling through tenant skins"
      style={{ position: 'relative' }}>
      <div
        className="st-branding-url"
        style={{
          border: 'var(--border-w) solid var(--ink)',
          background: 'var(--paper)',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          boxShadow: 'var(--shadow-sm)',
          marginBottom: 14
        }}>
        <span
          style={{ width: 10, height: 10, background: 'var(--acid)', border: '1.5px solid var(--ink)', flex: 'none' }}
        />
        <span className="st-branding-url-scheme" style={{ ...MONO, fontSize: 11, opacity: 0.6, flex: 'none' }}>
          https://
        </span>
        <span
          className="st-branding-url-domain"
          style={{ ...MONO, fontSize: 13, fontWeight: 700, transition: 'all .4s' }}>
          {s.domain}
        </span>
        <Pip bg="var(--acid)" style={{ fontSize: 9, padding: '2px 6px', flex: 'none' }}>
          VERIFIED
        </Pip>
      </div>

      <div
        style={{
          border: 'var(--border-w) solid var(--ink)',
          boxShadow: 'var(--shadow-xl)',
          background: s.bg,
          transition: 'background .5s'
        }}>
        <div
          style={{
            padding: '20px 22px',
            borderBottom: '2.5px solid var(--ink)',
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
          <span
            style={{
              width: 36,
              height: 36,
              background: s.accent,
              border: '2.5px solid var(--ink)',
              display: 'grid',
              placeItems: 'center',
              transform: 'rotate(-3deg)',
              flex: 'none',
              transition: 'background .5s'
            }}>
            <span style={{ ...MONO, fontSize: 14, fontWeight: 700, color: accentTextOnPaper }}>{s.name[0]}</span>
          </span>
          <div>
            <div
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 900,
                fontSize: 16,
                letterSpacing: '-0.01em',
                transition: 'all .4s'
              }}>
              {s.name}
            </div>
            <div style={{ ...MONO, fontSize: 10, opacity: 0.7, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              {s.tag}
            </div>
          </div>
        </div>

        <div style={{ padding: '24px 22px', background: 'var(--paper)' }}>
          <div
            style={{
              ...MONO,
              fontSize: 10,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              opacity: 0.6,
              marginBottom: 8
            }}>
            From {s.name}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 800,
              fontSize: 22,
              letterSpacing: '-0.02em',
              marginBottom: 10
            }}>
            Final delivery — Q2 shoot
          </div>
          <div style={{ ...MONO, fontSize: 12, opacity: 0.7, marginBottom: 16 }}>
            4 files · 2.4 GB · Expires in 30 days
          </div>

          {FILES.map(([name, size]) => (
            <div
              key={name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                border: '2px solid var(--ink)',
                background: 'var(--paper-2)',
                marginBottom: 6
              }}>
              <span
                style={{
                  width: 22,
                  height: 22,
                  background: s.bg,
                  border: '2px solid var(--ink)',
                  transition: 'background .5s'
                }}
              />
              <span style={{ ...MONO, fontSize: 12, fontWeight: 700, flex: 1 }}>{name}</span>
              <span style={{ ...MONO, fontSize: 10, opacity: 0.6 }}>{size}</span>
            </div>
          ))}

          <div
            aria-hidden
            style={{
              marginTop: 14,
              width: '100%',
              border: '2.5px solid var(--ink)',
              background: s.accent,
              color: accentTextOnPaper,
              padding: 12,
              fontFamily: 'var(--font-display)',
              fontWeight: 800,
              boxShadow: 'var(--shadow-sm)',
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transition: 'all .4s'
            }}>
            <span style={{ flex: 1, textAlign: 'center' }}>Download all</span>
            <span>→</span>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 29 }}>
        {SKINS.map((_, i) => (
          <span
            key={i}
            style={{
              width: i === skinIdx ? 22 : 8,
              height: 8,
              background: i === skinIdx ? 'var(--ink)' : 'var(--paper-2)',
              border: '1.5px solid var(--ink)',
              transition: 'all .3s'
            }}
          />
        ))}
      </div>
    </div>
  )
}
